// Per-tenant AI assistant config (model + API key) for the signed-in admin's
// tenant. The key is write-only: the API never returns it — only model + a
// masked status. Scoped to the caller's tenant via the admin JWT.
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

function authHeader() {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface AiConfig {
  provider: string;
  model: string | null;
  configured: boolean;
  keyLast4: string | null;
  configuredAt: string | null;
}

export async function getAiConfig(): Promise<AiConfig> {
  const { data } = await axios.get(`${API_URL}/tenants/ai-config`, { headers: authHeader() });
  return data;
}

export async function saveAiConfig(input: { provider?: string; model?: string; apiKey?: string }): Promise<AiConfig> {
  const { data } = await axios.put(`${API_URL}/tenants/ai-config`, input, { headers: authHeader() });
  return data;
}

export async function clearAiConfig(): Promise<AiConfig> {
  const { data } = await axios.delete(`${API_URL}/tenants/ai-config`, { headers: authHeader() });
  return data;
}

// --- Knowledge base: per-tenant HR documents for RAG (ai-agent-service) ---

export interface HRDoc {
  id: string;
  name: string;
  content_type?: string | null;
  chunk_count: number;
  status: string;
  created_at?: string | null;
}

export async function listDocuments(): Promise<HRDoc[]> {
  const { data } = await axios.get(`${API_URL}/ai/documents`, { headers: authHeader() });
  return data;
}

export async function uploadDocument(file: File): Promise<{ id: string; name: string; chunk_count: number }> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await axios.post(`${API_URL}/ai/documents`, form, { headers: authHeader() });
  return data;
}

export async function deleteDocument(id: string): Promise<void> {
  await axios.delete(`${API_URL}/ai/documents/${id}`, { headers: authHeader() });
}

// Curated current Claude models (models evolve — a custom field covers new ones).
// --- Provider + model catalogue (single source of truth) -------------------
// DESIGN: the PROVIDER is a fixed choice (it maps to a code path + key format),
// so it's a real dropdown. The MODEL is deliberately NOT locked — models evolve
// faster than we deploy, so every model field is a free-entry autocomplete that
// merely SUGGESTS these current ids. A brand-new model works the instant the
// provider ships it, with no code change. Add suggestions here as convenience.

export interface Choice { value: string; label: string }

export const PROVIDERS: Choice[] = [
  { value: 'anthropic', label: 'Anthropic (Claude)' },
  { value: 'openai', label: 'OpenAI (GPT)' },
];

export const CLAUDE_MODELS: Choice[] = [
  { value: 'claude-opus-4-8', label: 'Claude Opus 4.8 — most capable' },
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 — balanced (recommended)' },
  { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 — fast & low-cost' },
];

export const OPENAI_MODELS: Choice[] = [
  { value: 'gpt-4o', label: 'GPT-4o — balanced (recommended)' },
  { value: 'gpt-4o-mini', label: 'GPT-4o mini — fast & low-cost' },
  { value: 'gpt-4.1', label: 'GPT-4.1 — most capable' },
];

// Suggested models per provider (empty-safe fallback = Claude list).
export const MODELS_BY_PROVIDER: Record<string, Choice[]> = {
  anthropic: CLAUDE_MODELS,
  openai: OPENAI_MODELS,
};

export const DEFAULT_MODEL_BY_PROVIDER: Record<string, string> = {
  anthropic: 'claude-sonnet-4-6',
  openai: 'gpt-4o',
};

// A representative key prefix per provider, for input placeholders only.
export const KEY_PLACEHOLDER_BY_PROVIDER: Record<string, string> = {
  anthropic: 'sk-ant-...',
  openai: 'sk-...',
};

export const modelsFor = (provider?: string): Choice[] =>
  MODELS_BY_PROVIDER[(provider || 'anthropic').toLowerCase()] || CLAUDE_MODELS;
